import React, { FormEvent, useEffect, useState } from 'react';
import { IonButtons, IonText } from '@ionic/react';
import { useUserState } from '../../hooks/context/useUserState';
import Input from '../../components/Input/Input';
import PageLayout from '../../layouts/PageLayout/PageLayout';
import Form from '../../components/Form/Form';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useLogin } from '../../api/hooks/useLogin';
import { getIsIos, getIsMobile, translatedToast } from '../../utils/helpers';
import { useHistory } from 'react-router-dom';
import './Login.css';
import Checkbox from '../../components/Checkbox/Checkbox';
import Dot from '../../components/Dot/Dot';
import { useCfgParam } from '../../hooks/useCfgParam';
import Button from '../../components/Button/Button';
import ArrowIcon from '../../components/ArrowIcon/ArrowIcon';
import Message from '../../components/Message/Message';
import Container from '../../components/Container/Container';
import clsx from 'clsx';
import SeptimLoadingSpinner from '../../components/Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import useRoutes from '../../hooks/useRoutes';

const Login: React.FC = () => {
  const {
    state: { usePassword, useOtp, endpointCorrect },
  } = useUserState();
  const { t } = useTranslate();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [otpPassword, setOtpPassword] = useState<string>('');
  const [keepLoggedIn, setKeepLoggedIn] = useState<boolean>(false);
  const { login, isPending } = useLogin();
  const isMobile = getIsMobile();
  const history = useHistory();
  const allowPermanentLogin = useCfgParam(
    'loginOptions',
    'allowPermanentLogin'
  );
  const routes = useRoutes();
  const isIos = getIsIos();

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    if (!username) {
      translatedToast('toasts.missingUsername');
      return;
    }
    if (!usePassword && !useOtp) {
      translatedToast('toasts.otpOrPasswordNotSupport');
    }

    if (usePassword && useOtp) {
      if (password && otpPassword) {
        translatedToast('toasts.enterOtpOrPasswordNotBoth');
        return;
      } else if (!password && !otpPassword) {
        translatedToast('toasts.enterOtpOrPassword');
        return;
      }
    } else if (usePassword && !password) {
      translatedToast('toasts.enterPassword');
      return;
    } else if (useOtp && !otpPassword) {
      translatedToast('toasts.enterOtp');
      return;
    }
    if (useOtp && otpPassword) {
      login({
        body: {
          userLogin: username,
          userOtp: otpPassword,
        },
        keepLoggedIn,
      });
    } else if (usePassword && password) {
      login({
        body: {
          userLogin: username,
          userPassword: password,
        },
        keepLoggedIn,
      });
    }
  };

  useEffect(() => {
    if (!endpointCorrect && isMobile) {
      history.push(routes.setEndpoint());
    }
  }, [endpointCorrect, isMobile]);

  const settingsClickHandler = () => {
    history.push(routes.setEndpoint());
  };

  return (
    <PageLayout className={clsx('login-page', isMobile && 'login-page-mobile')}>
      <Container className="login-container">
        <div className="white-box login-content">
          <div
            style={{ visibility: isPending ? 'hidden' : 'visible' }}
            aria-hidden={isPending || undefined}
          >
            <IonText className="login-header" color={'secondary'}>
              <h1>
                {t('welcomeBack')}
                <Dot />
              </h1>
            </IonText>
            <Form
              onSubmit={handleLogin}
              submitMessage={'logIn'}
              className={'login-form'}
            >
              <Input
                onChange={setUsername}
                value={username}
                name={'username'}
                label={t('username')}
              />
              {usePassword && (
                <Input
                  onChange={setPassword}
                  value={password}
                  name={'password'}
                  label={t('password')}
                  type={'password'}
                />
              )}
              {useOtp && (
                <Input
                  onChange={setOtpPassword}
                  value={otpPassword}
                  name={'otpPassword'}
                  label={t('otpPassword')}
                  type={'password'}
                />
              )}
              {allowPermanentLogin && !isMobile && (
                <Checkbox
                  onChange={setKeepLoggedIn}
                  value={keepLoggedIn}
                  label={t('keepLoggedIn')}
                />
              )}
            </Form>
          </div>
          {isPending && (
            <SeptimLoadingSpinner className="login-content-spinner" />
          )}
        </div>
        {isMobile && (
          <div
            className={clsx('login-footer', isIos && 'ios-swipe-bar-padding')}
          >
            <IonButtons>
              <Button color="primary" onClick={settingsClickHandler}>
                <ArrowIcon direction="left" />
                <Message id="endpoint" />
              </Button>
            </IonButtons>
          </div>
        )}
      </Container>
    </PageLayout>
  );
};

export default Login;
